import { FilePrefetch } from "./filePrefetch";
import { fileName } from "./fileNames";
import type { createProjectApi } from "../../worker/client/projectApi";
import type {
  CreateFile,
  ProjectFile,
  Revision,
} from "../../worker/shared/contracts";

export interface OpenDocument {
  file: ProjectFile;
  bytes: ArrayBuffer;
  revision: number;
}
export interface DocumentStore {
  prefetchFile?(projectId: string, file: ProjectFile): void;
  clearPrefetch?(): void;
  listFiles(projectId: string): Promise<ProjectFile[]>;
  createFile(projectId: string, input: CreateFile): Promise<ProjectFile>;
  openFile(projectId: string, fileId: string, knownFile?: ProjectFile): Promise<OpenDocument>;
  saveFile(
    projectId: string,
    fileId: string,
    bytes: string | Blob | ArrayBuffer,
    revision: number,
  ): Promise<ProjectFile>;
  renameFile(
    projectId: string,
    fileId: string,
    name: string,
  ): Promise<ProjectFile>;
  moveFile(
    projectId: string,
    fileId: string,
    parentId: string | null,
  ): Promise<ProjectFile>;
  deleteFile(projectId: string, fileId: string): Promise<void>;
  listRevisions(projectId: string, fileId: string): Promise<Revision[]>;
  openRevision(
    projectId: string,
    fileId: string,
    revision: number,
  ): Promise<ArrayBuffer>;
  restoreRevision(
    projectId: string,
    fileId: string,
    revision: number,
    expectedRevision: number,
  ): Promise<ProjectFile>;
}
/** The only cloud document boundary. Editors never construct HTTP requests or storage keys. */
export function createDocumentStore(
  api: ReturnType<typeof createProjectApi>,
): DocumentStore {
  const reads = new FilePrefetch(async (projectId, fileId, knownFile) => {
      const file = knownFile && knownFile.id === fileId && knownFile.projectId === projectId && knownFile.currentRevision > 0 ? knownFile : (await api.getFile(projectId, fileId)).file;
      if (file.kind === "folder")
        throw new Error("Folders do not have document content.");
      const content = file.currentRevision
        ? await api.loadFile(projectId, fileId)
        : { bytes: new ArrayBuffer(0), revision: 0 };
      return {
        file: { ...file, currentRevision: content.revision },
        ...content,
      };
  });
  const save: DocumentStore["saveFile"] = async (
    projectId,
    fileId,
    bytes,
    revision,
  ) => {
    if (!Number.isSafeInteger(revision) || revision < 0)
      throw new Error("A valid base revision is required to save.");
    reads.clear(projectId, fileId);
    try { return (await api.saveFile(projectId, fileId, bytes, revision)).file; }
    finally { reads.clear(projectId, fileId); }
  };
  return {
    async listFiles(projectId) {
      return (await api.listFiles(projectId)).files;
    },
    async createFile(projectId, input) {
      return (await api.createFile(projectId, { ...input, name: fileName({ ...input, extension: input.extension || null }) })).file;
    },
    openFile: (projectId, fileId, file) => reads.open(projectId, fileId, file),
    prefetchFile: (projectId, file) => reads.prefetch(projectId, file),
    clearPrefetch: () => reads.clear(),
    saveFile: save,
    async renameFile(projectId, fileId, name) {
      reads.clear(projectId, fileId);
      try {
        const { file } = await api.getFile(projectId, fileId);
        name = fileName({ ...file, name });
        const dot = name.lastIndexOf(".");
        const extension = file.kind === "folder" || dot < 0 ? null : name.slice(dot + 1);
        return (await api.updateFile(projectId, fileId, { name, extension })).file;
      } finally { reads.clear(projectId, fileId); }
    },
    async moveFile(projectId, fileId, parentId) {
      reads.clear(projectId, fileId);
      try { return (await api.updateFile(projectId, fileId, { parentId })).file; }
      finally { reads.clear(projectId, fileId); }
    },
    async deleteFile(projectId, fileId) {
      reads.clear(projectId, fileId);
      try { await api.deleteFile(projectId, fileId); }
      finally { reads.clear(projectId, fileId); }
    },
    async listRevisions(projectId, fileId) {
      return (await api.listRevisions(projectId, fileId)).revisions;
    },
    openRevision: (projectId, fileId, revision) =>
      api.loadRevision(projectId, fileId, revision),
    async restoreRevision(projectId, fileId, revision, expectedRevision) {
      return save(
        projectId,
        fileId,
        await api.loadRevision(projectId, fileId, revision),
        expectedRevision,
      );
    },
  };
}
